import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { getExtension } from "./components/chat/components/upload-dialog";
import { MAX_FILE_SIZE, showUploadError } from "./components/chat/chat";
import { ApiError } from "@/types/types";
import { Message } from "../../../../lib/chat-store/types";
import { MediaItem } from "@/lib/media/shared";

type UploadStatus = "PRESIGNING" | "UPLOADING" | "UPLOADED" | "ERROR";

export type UploadItem = {
  id: string;
  file: File;
  PreviewUrl: string;

  Status: UploadStatus;
  progress: number;

  MediaId?: string;
  UploadUrl?: string;
};

type UploadStore = {
  uploads: UploadItem[];

  addFiles: (files: File[]) => void;
  removeFile: (id: string) => void;
  clear: () => void;

  getUploadedMediaIds: () => string[];

  updateUpload: (id: string, patch: Partial<UploadItem>) => void;
};

export const useUploadStore = create<UploadStore>((set, get) => ({
  uploads: [],

  updateUpload: (id, patch) => {
    set((s) => ({
      uploads: s.uploads.map((u) => (u.id === id ? { ...u, ...patch } : u)),
    }));
  },

  addFiles: (files) => {
    files.forEach((file) => {
      const error = validateFile(file);

      if (error) {
        showUploadError(error);
        return;
      }
      const id = uuidv4();

      const upload: UploadItem = {
        id,
        file,
        PreviewUrl: URL.createObjectURL(file),
        Status: "PRESIGNING",
        progress: 0,
      };

      set((s) => ({ uploads: [...s.uploads, upload] }));

      startUpload(id, file);
    });
  },

  removeFile: (id) => {
    set((s) => ({
      uploads: s.uploads.filter((u) => u.id !== id),
    }));
  },

  clear: () => {
    // clean object URLs to avoid memory leaks
    get().uploads.forEach((u) => URL.revokeObjectURL(u.PreviewUrl));

    set({ uploads: [] });
  },

  getUploadedMediaIds: () => {
    return get()
      .uploads.filter((u) => u.Status === "UPLOADED" && u.MediaId)
      .map((u) => u.MediaId as string);
  },
}));

async function getPresign(file: File) {
  const fileExtension = getExtension(file.name);

  if (!fileExtension) {
    throw new Error("Missing file extension");
  }
  const body = {
    MediaCategory: resolveMediaCategory(file),
    ProcessingProfile: resolveProcessingProfile(file),
    ContentType: file.type,
    FileExtension: fileExtension,
    OriginalFileName: file.name,
  };
  console.log(body);
  const res = await fetch("/api/media/presign", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let errorMessage = "An unexpected error occurred.";

    try {
      const data = (await res.json()) as ApiError;
      errorMessage = data.message || errorMessage;
    } catch {
      const text = await res.text();
      errorMessage = text || errorMessage;
    }

    toast(errorMessage, { type: "error" });

    throw new Error(errorMessage);
  }

  return res.json() as Promise<{
    MediaId: string;
    UploadUrl: string;
    Key: string;
  }>;
}

function uploadFile(file: File, url: string, onProgress: (p: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress((e.loaded / e.total) * 100);
      }
    };

    xhr.onload = () => resolve();
    xhr.onerror = reject;

    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.send(file);
  });
}

async function startUpload(id: string, file: File) {
  const { updateUpload } = useUploadStore.getState();

  try {
    // 1. presign
    const presign = await getPresign(file);

    updateUpload(id, {
      Status: "UPLOADING",
      MediaId: presign.MediaId,
      UploadUrl: presign.UploadUrl,
    });

    // 2. upload
    await uploadFile(file, presign.UploadUrl, (progress) => {
      updateUpload(id, { progress });
    });

    // 3. done
    updateUpload(id, {
      Status: "UPLOADED",
      progress: 100,
    });
  } catch (err) {
    updateUpload(id, {
      Status: "ERROR",
    });
  }
}

export function resolveMediaCategory(
  file: File,
): "image" | "video" | "document" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";

  return "document";
}

export function getCurrentCategory(uploads: UploadItem[]): string | null {
  if (uploads.length === 0) return null;
  return resolveMediaCategory(uploads[0].file);
}

export function resolveProcessingProfile(file: File): string {
  if (file.type.startsWith("image/")) return "chat_image";
  if (file.type.startsWith("video/")) return "video";

  return "document";
}

export function validateFile(file: File): string | null {
  if (!ALLOWED_MIME.has(file.type)) {
    return "file-invalid-type";
  }

  if (file.size > MAX_FILE_SIZE) {
    return "too-large-err";
  }

  return null;
}

export const ALLOWED_MIME = new Set([
  // images
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",

  // video
  "video/mp4",
  "video/webm",
  "video/quicktime",

  // documents
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

export function validateAndAddFiles(
  files: File[],
  uploads: UploadItem[],
  addFiles: (files: File[]) => void,
) {
  if (files.length === 0) return;

  const currentCategory =
    uploads.length > 0 ? resolveMediaCategory(uploads[0].file) : null;

  if (!currentCategory) {
    const firstCategory = resolveMediaCategory(files[0]);

    const allSame = files.every(
      (file) => resolveMediaCategory(file) === firstCategory,
    );

    if (!allSame) {
      showUploadError("different-categories");
      return;
    }

    addFiles(files);
    return;
  }

  const allMatch = files.every(
    (file) => resolveMediaCategory(file) === currentCategory,
  );

  if (!allMatch) {
    showUploadError("different-categories");
    return;
  }

  addFiles(files);
}

export function handleFilesInput(
  files: File[],
  uploads: UploadItem[],
  addFiles: (files: File[]) => void,
) {
  if (files.length === 0) return;

  // 🔥 max files check (same as dropzone)
  if (uploads.length + files.length > 10) {
    showUploadError("too-many-err");
    return;
  }

  const validFiles: File[] = [];

  for (const file of files) {
    const error = validateFile(file);

    if (error) {
      showUploadError(error);
    } else {
      validFiles.push(file);
    }
  }

  if (validFiles.length === 0) return;

  validateAndAddFiles(validFiles, uploads, addFiles);
}
